//#region IMPORTS
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

// COMPONENTS
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KOGCardStatus from '@/main/components/shared/cardStatus/cardStatus';
import KogKeyboardDatePickerContainer from '@/main/components/shared/datepicker/keyboardDatePickerContainer';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import VehicleAutoCompleteComponent from '@/main/components/shared/vehicle/VehicleAutoCompleteComponent';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import SearchIcon from '@mui/icons-material/Search';

// API/MODELS/SERVICES
import { quoteApi } from '@/main/api';
import { KeyValue } from '@/main/models/common';
import {
  BodyShopFilterRequest,
  InsurerFilterRequest,
  QuoteCountStatusRequest,
  QuoteFilterRequest,
  ReasonFilterRequest,
  StateFilterRequest,
  SupplierFilterRequest
} from '@/main/models/request';
import { QuoteResponseCount } from '@/main/models/response';
import { Controls } from '@/main/services/constants/constantsControl';
import {
  enumToKeyValue,
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  isShowControlByElementId,
  removeEmptyValues
} from '@/main/services/utils';
import {
  formatDateToISOString,
  getKeysIgnoreValidationsDate,
  initalDateForm,
  onLoadDateRangePicker,
  validateFormDate
} from '@/main/services/utilsDate';
import {
  userIsAdmin,
  userIsBodyshop,
  userIsInsurer,
  userIsSupplier
} from '@/main/services/utilsUser';

// ENUMS
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { OrderStatusEnum } from '@/main/services/enum/OrderStatusEnum';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { VehicleAutoCompleteTypeEnum } from '@/main/services/enum/VehicleAutoCompleteTypeEnum';

// REDUX
import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import { useCountryReducer } from '@/main/store/country';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import {
  getQuoteCountStatus,
  useQuoteCountStatusReducer
} from '@/main/store/quoteCountStatus';
import { useQuoteStatusReducer } from '@/main/store/quoteStatus';
import { getReason, useReasonReducer } from '@/main/store/reason';
import { getStatesUser, useStateReducer } from '@/main/store/state';
import {
  getSuppliers,
  resetReducerSuppliers,
  useSupplierReducer
} from '@/main/store/supplier';
import {
  deleteUserPredefinedSearch,
  formSchema,
  getDetailsUserPredefinedSearch,
  getFavoriteSearch,
  saveFavoriteSearch,
  saveUserPredefinedSearch
} from './searchOrderUtils';

// STYLES & COMPONENTS LOCAL
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import KogAutoCompleteControlledWithDelete from '@/main/components/shared/inputAutocompleteDelete/autoCompleteControlledWithDelete';
import BasicModal from '@/main/components/shared/modal/basicModal';
import { ButtonsContainer } from '@/main/components/shared/modal/style';
import ModalDeletePredefinedSearch from '@/main/components/shared/modalPredefinedSearch/ModalDeletePredefinedSearch';
import ModalSavePredefinedSearch from '@/main/components/shared/modalPredefinedSearch/ModalSavePredefinedSearch';
import RenderTooltipMessage from '@/main/components/shared/renderTooltipMessage/renderTooltipMessage';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { SortByOldestEnum } from '@/main/services/enum/SortByOldestEnum';
import { ApplicationState } from '@/main/store';
import {
  getPredefinedSearch,
  usePredefinedSearchReducer
} from '@/main/store/predefinedSearch';
import {
  getPreQuoteType,
  usePreQuoteTypeReducer
} from '@/main/store/preQuoteType';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import ModalImportQuote from '../components/modalImportQuote/modalImportQuote';
import { IGridData, INITIAL_GRID_STATE_ORDER_SCREEN } from '../orderScreen';
import * as S from './styles';
import { useIsWrapped } from '@/main/screens/order/viewOrder/filtersOrderScreen/useContainerWrap';

//#endregion

interface IFiltersOrderScreenProps {
  showFilters: boolean;
  isRefreshGrid: boolean;
  setIsRefreshGrid: React.Dispatch<React.SetStateAction<boolean>>;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingInfiniteScroll: React.Dispatch<React.SetStateAction<boolean>>;
  setHasShowMore: React.Dispatch<React.SetStateAction<boolean>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setFormFilters: React.Dispatch<
    React.SetStateAction<QuoteFilterRequest | null>
  >;
  setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
  handleUpdateQuoteCountStatus: (
    startDate?: Date | null,
    finalDate?: Date | null
  ) => void;
  onTotalNumberOfRecordsLoaded: (total: number) => void;
}

function FiltersOrderScreen({
  showFilters,
  isRefreshGrid,
  setIsRefreshGrid,
  setShowFilters,
  setIsLoadingInfiniteScroll,
  setHasShowMore,
  setPageIndex,
  setFormFilters,
  setGridData,
  handleUpdateQuoteCountStatus,
  onTotalNumberOfRecordsLoaded
}: IFiltersOrderScreenProps) {
  //#region Reducers
  const { insurers: insurerReducer } = useInsurerReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { quoteStatus: quoteStatusReducer } = useQuoteStatusReducer();
  const { bodyshops: bodyshopReducer } = useBodyshopReducer();
  const { suppliers: supplierReducer } = useSupplierReducer();
  const { reason: reasonReducer } = useReasonReducer();
  const { statesUser: stateReducer } = useStateReducer();
  const { preQuoteType: preQuoteTypeReducer } = usePreQuoteTypeReducer();
  const { domainPredefinedSearch } = usePredefinedSearchReducer();
  const { ref: checkboxAreaRef, isWrapped } = useIsWrapped<HTMLDivElement>();
  //#endregion

  //#region Hooks
  const dispatch = useDispatch();
  const { quoteCountStatus: quoteCountStatusReducer } =
    useQuoteCountStatusReducer();
  const parameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSearchOrderDaysInterval
  );
  const singleStatusSearchOrderDaysInterval = useParameterByTypeScreenReducer(
    ParameterTypeEnum.SingleStatusSearchOrderDaysInterval
  );
  const PARAMETER_LENTGH_FILTER_CLAIM = useParameterByTypeScreenReducer(
    ParameterTypeEnum.OrderScreenFilterClaimLength
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const cardStatusQueryParams = searchParams.get('idCardStatus');
  const showCards = isShowControlByElementId(Controls.IdSearchOrderStatusCards);
  const preQuoteType =
    isShowControlByElementId(Controls.IdPreQuoteGlass) ||
    isShowControlByElementId(Controls.IdPreQuoteTire);
  const reduxControls = useSelector(
    (state: ApplicationState) => state.control.controlsByScreen
  );

  const INITIAL_FORM_STATE = {
    quoteFilterVehicle: '',
    startDate: null as Date | null,
    finalDate: null as Date | null,
    idQuoteStatus: [] as KeyValue[],
    states: [] as KeyValue[],
    reason: null as KeyValue | null,
    insurer: null as KeyValue | null,
    supplier: null as KeyValue | null,
    bodyshopBranches: [] as KeyValue[],
    subModel: '' as string,
    vehicleManufacturer: '' as string,
    vehicleModel: '' as string,
    vehicle: '' as string,
    idPreQuoteType: null as KeyValue | null,
    havingItemCanceledBySupplier: false as boolean,
    predefinedSearch: null as KeyValue | null,
    sortByOldest: null as KeyValue | null,
    pendingApproval: false as boolean
  };

  type ISearchQuoteForm = typeof INITIAL_FORM_STATE;
  const {
    control,
    queryParamsValues,
    customHandleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    getValues,
    trigger
  } = useFormQueryParams<ISearchQuoteForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      insurer: insurerReducer,
      supplier: supplierReducer,
      reason: reasonReducer,
      idPreQuoteType: preQuoteTypeReducer,
      predefinedSearch: domainPredefinedSearch
    },
    onSubmit: onSubmit
  });
  //#endregion

  //#region States
  const [hasClear, setHasClear] = useState<boolean>(false);
  const [isIgnoreValidationsDate, setIsIgnoreValidationsDate] =
    useState<boolean>(false);
  const [isInitialLoadScreen, setIsInitialLoadScreen] =
    useState<boolean>(false);
  const [
    openModalConfirmationFavoriteSearch,
    setOpenModalConfirmationFavoriteSearch
  ] = useState<boolean>(false);
  const [loadingFavoriteSearch, setloadingFavoriteSearch] =
    useState<boolean>(false);
  const [defaultValueVehicleFavorite, setDefaultValueVehicleFavorite] =
    useState<string>('');
  const [reducersLoaded, setReducersLoaded] = useState(false);
  const [
    openModalConfirmationSavePredefinedSearch,
    setOpenModalConfirmationSavePredefinedSearch
  ] = useState<boolean>(false);
  const [loadingPredefinedSearch, setloadingPredefinedSearch] =
    useState<boolean>(false);
  const [
    openModalConfirmationDeletePredefinedSearch,
    setOpenModalConfirmationDeletePredefinedSearch
  ] = useState<boolean>(false);
  const [predefinedSearchToDelete, setPredefinedSearchToDelete] =
    useState<KeyValue | null>(null);
  const watcherStatus = watch('idQuoteStatus');
  const watcherCanceledBySupplier = watch('havingItemCanceledBySupplier');
  const watcherPendingApproval = watch('pendingApproval');
  const watcherSortByOldest = watch('sortByOldest');
  //#endregion

  //#region variables and default values
  const hasBodyshopDefault =
    userIsBodyshop() && bodyshopReducer?.data?.length === 1;
  const isUserSupplierLogged = userIsSupplier();
  const isUserInsurerLogged = userIsInsurer();
  const isUserBodyShopLogged = userIsBodyshop();

  const optionDefaultBodyshopDrop = getDefaultOptionDrop(bodyshopReducer.data);
  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierReducer.data);
  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );
  const optionDefaultPreQuoteType = getDefaultOptionDrop(
    preQuoteTypeReducer.data
  );

  const isDisabledInsurersDrop = () => userIsInsurer();
  const isDisabledBodyshopDrop = () =>
    bodyshopReducer.data === null || hasBodyshopDefault;
  const isDisabledSupplierDrop = () =>
    supplierReducer.data === null || userIsSupplier();
  const hasStatusOptions = () => getStatusDropFiltered().length > 0;
  const isDisabledStatusDrop = () =>
    preQuoteType ||
    watcherCanceledBySupplier ||
    !hasStatusOptions() ||
    watcherPendingApproval;
  const isDisabledPreQuoteTypeDrop = () =>
    preQuoteType && preQuoteTypeReducer?.data?.length === 1;
  const didInitSearch = React.useRef(false);
  const isAutoSubmit = React.useRef(false);
  const showPredefinedSearches = isShowControlByElementId(
    Controls.IdPredefinedSearch
  );
  const showFavouriteSearches = isShowControlByElementId(
    Controls.IdFavouriteSearch
  );
  const showSortByOldest = isShowControlByElementId(
    Controls.IdSortByOldestSearch
  );
  const sortByOldestOptions = enumToKeyValue(SortByOldestEnum, 'information');
  const showPendingApproval = isShowControlByElementId(
    Controls.IdSearchOrderPendingApproval
  );
  //#endregion

  //#region Core Functions
  const ID_SCREEN = 'id-searchOrder';
  const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;

  useEffect(() => {
    const requestCount: QuoteCountStatusRequest = {
      startDate: initalDateForm(parameterRangeDays),
      finalDate: initalDateForm()
    };

    dispatch(getQuoteCountStatus(requestCount));
  }, []);

  function handleOpenModalFavoriteSearch() {
    setOpenModalConfirmationFavoriteSearch(true);
  }

  async function handleConfirmModalFavoriteSearch() {
    setloadingFavoriteSearch(true);
    trigger()
      .then(isValid => {
        if (!isValid) {
          setloadingFavoriteSearch(false);
          const filterIncalidMsg = getTranslateByKey(
            'information.fieldInvalid',
            true
          ).replace('{0}', getTranslateByKey('information.filters'));
          toast.error(filterIncalidMsg);
          setOpenModalConfirmationFavoriteSearch(false);
          return;
        }
        const rawValues = getValues();

        if (
          hasInvalidSortByOldestStatus(
            rawValues.sortByOldest,
            rawValues.idQuoteStatus
          )
        ) {
          setloadingFavoriteSearch(false);
          toast.error(
            getTranslateByKey(
              'information.predefinedSearchMultipleStatusSortByOldest'
            )
          );
          setOpenModalConfirmationSavePredefinedSearch(false);
          return;
        }

        const sortByOldestKey = rawValues.sortByOldest?.key;
        const isSortByOldest =
          !isNullUndefined(sortByOldestKey) &&
          sortByOldestKey !== SortByOldestEnum.fillinarange;
        if (!isSortByOldest) {
          const ignoreValidationsDateSubmit =
            rawValues.quoteFilterVehicle.length >=
            PARAMETER_LENTGH_FILTER_CLAIM;
          const keysIgnoreValidationsDateSubmit = getKeysIgnoreValidationsDate(
            ignoreValidationsDateSubmit,
            rawValues.startDate,
            rawValues.finalDate
          );

          const rangeParameter = singleStatusSelected
            ? singleStatusSearchOrderDaysInterval
            : parameterRangeDays;

          validateFormDate(
            rawValues.startDate,
            rawValues.finalDate,
            rangeParameter,
            keysIgnoreValidationsDateSubmit
          );
        }

        const parsed = formSchema.safeParse(rawValues);

        if (!parsed.success) {
          setloadingFavoriteSearch(false);
          setOpenModalConfirmationFavoriteSearch(false);
          return;
        }
        const request = parsed.data;

        saveFavoriteSearch(request)
          .then(result => {
            if (result) {
              toast.success(
                getTranslateByKey('information.favoriteSearchSavedSuccessfully')
              );
            } else {
              toast.error(
                getTranslateByKey('information.favoriteSearchSavedError')
              );
            }
          })
          .finally(() => {
            setloadingFavoriteSearch(false);
            setOpenModalConfirmationFavoriteSearch(false);
          });
      })
      .catch(() => {
        setloadingFavoriteSearch(false);
        setOpenModalConfirmationFavoriteSearch(false);
      });
  }

  function getCountStatusFilteredBySupplierUser(data: QuoteResponseCount[]) {
    return data.filter(
      x =>
        Number(x.keyStatus) !== ProcurementStatusEnum.Open &&
        Number(x.keyStatus) !== ProcurementStatusEnum.Manual
    );
  }

  function getStatusFiltered() {
    if (
      quoteCountStatusReducer.isLoading ||
      isEmpty(quoteCountStatusReducer.data)
    )
      return [];
    const filteredStatus = isUserSupplierLogged
      ? getCountStatusFilteredBySupplierUser(quoteCountStatusReducer.data)
      : quoteCountStatusReducer.data;

    return filteredStatus.map(item => ({
      key: Number(item.keyStatus),
      value: item.nameStatus,
      count: item.value ?? 0
    }));
  }

  // This function is used to create initial request with default values
  function constructInitialRequest(): ISearchQuoteForm {
    const request: ISearchQuoteForm = cloneDeep(INITIAL_FORM_STATE);
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    request.startDate = firstDateResult;
    request.finalDate = secondDateResult;

    if (preQuoteType) {
      const preQuoteKeyValue = {
        key: ProcurementStatusEnum.PreQuote,
        value: getTranslateByKey('information.prequote')
      };
      setValue('idQuoteStatus', [preQuoteKeyValue]);
      request.idQuoteStatus = [preQuoteKeyValue];

      if (preQuoteTypeReducer.data?.length === 1) {
        setValue('idPreQuoteType', optionDefaultPreQuoteType);
        request.idPreQuoteType = optionDefaultPreQuoteType;
      }
    }

    return cloneDeep(request);
  }

  function checkValidationDates(inputClaimNR: string) {
    if (
      inputClaimNR.length >= PARAMETER_LENTGH_FILTER_CLAIM &&
      !isIgnoreValidationsDate
    ) {
      setIsIgnoreValidationsDate(true);
      return true;
    }
    if (
      inputClaimNR.length < PARAMETER_LENTGH_FILTER_CLAIM &&
      isIgnoreValidationsDate
    ) {
      setIsIgnoreValidationsDate(false);
      return false;
    }
    return null;
  }

  function loadDataWithCardStatusParams() {
    const request = constructInitialRequest();
    const requestCount: QuoteCountStatusRequest = {
      startDate: initalDateForm(parameterRangeDays),
      finalDate: initalDateForm()
    };
    const statusFilter: KeyValue | null = {
      key: String(cardStatusQueryParams),
      value: ''
    };
    request.idQuoteStatus = [statusFilter];
    dispatch(getQuoteCountStatus(requestCount));
    onSubmit(request);
  }

  function getStatusFilteredBySupplierUser(data: KeyValue[]) {
    return data.filter(
      (x: KeyValue) =>
        Number(x.key) !== ProcurementStatusEnum.Open &&
        Number(x.key) !== ProcurementStatusEnum.Manual
    );
  }

  function getPreQuoteStatus(data: KeyValue[]) {
    return data.filter(
      (x: KeyValue) => Number(x.key) === ProcurementStatusEnum.PreQuote
    );
  }

  function getStatusDropFiltered() {
    if (
      quoteStatusReducer.data !== null &&
      quoteStatusReducer.data.length > 0
    ) {
      if (isUserSupplierLogged) {
        return getStatusFilteredBySupplierUser(quoteStatusReducer.data);
      } else if (preQuoteType) {
        return getPreQuoteStatus(quoteStatusReducer.data);
      }

      return quoteStatusReducer.data;
    }

    return [];
  }

  function getStateFilteredByCountry() {
    return stateReducer.data;
  }

  function existsInDrop(data: KeyValue[], nameField: any) {
    const fieldKeySelected = getValues(nameField)?.key;
    const existsInData = data.find(item => item.key === fieldKeySelected);

    if (!existsInData) {
      setValue(nameField, null);
    }
  }

  //#endregion

  //#region Async Functions
  async function onSubmit(
    formInputs: ISearchQuoteForm,
    isQueryParams: boolean = false
  ) {
    const ignoreValidationsDateSubmit =
      formInputs.quoteFilterVehicle.length >= PARAMETER_LENTGH_FILTER_CLAIM;
    const keysIgnoreValidationsDateSubmit = getKeysIgnoreValidationsDate(
      ignoreValidationsDateSubmit,
      formInputs.startDate,
      formInputs.finalDate
    );

    const sortByOldestKey = formInputs.sortByOldest?.key;
    const isSortByOldest =
      !isNullUndefined(sortByOldestKey) &&
      sortByOldestKey !== SortByOldestEnum.fillinarange;
    const isFillRange = sortByOldestKey === SortByOldestEnum.fillinarange;

    const statusCount = formInputs.idQuoteStatus?.length ?? 0;
    const hasSortByOldest = !!formInputs.sortByOldest;

    if (hasSortByOldest && statusCount > 1) {
      toast.error(getTranslateByKey('information.sortByOldestMultipleStatus'));
      return;
    }

    if (isQueryParams && !isSortByOldest) {
      if (isNullUndefined(formInputs.startDate)) {
        const valueStartDate = ignoreValidationsDateSubmit
          ? null
          : initalDateForm(parameterRangeDays);

        formInputs.startDate = valueStartDate;
        setValue('startDate', valueStartDate);
      }
      if (isNullUndefined(formInputs.finalDate)) {
        const valueFinalDate = ignoreValidationsDateSubmit
          ? null
          : initalDateForm();

        formInputs.finalDate = valueFinalDate;
        setValue('finalDate', valueFinalDate);
      }
    }

    if (!isSortByOldest) {
      const rangeParameter =
        statusCount == 1
          ? singleStatusSearchOrderDaysInterval
          : parameterRangeDays;

      validateFormDate(
        formInputs.startDate,
        formInputs.finalDate,
        rangeParameter,
        keysIgnoreValidationsDateSubmit
      );
    }

    if (
      hasInvalidSortByOldestStatus(
        formInputs.sortByOldest,
        formInputs.idQuoteStatus
      )
    ) {
      toast.error(
        getTranslateByKey(
          'information.favoriteSearchMultipleStatusSortByOldest'
        )
      );
      return;
    }

    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: QuoteFilterRequest = {
      startDate: isNullUndefined(formInputs.startDate)
        ? ''
        : formatDateToISOString(formInputs.startDate),
      finalDate: isNullUndefined(formInputs.finalDate)
        ? ''
        : formatDateToISOString(formInputs.finalDate),
      quoteFilterVehicle: formInputs?.quoteFilterVehicle
        ? String(formInputs.quoteFilterVehicle)
        : null,
      guidBodyshopBranches: !!formInputs.bodyshopBranches
        ? formInputs.bodyshopBranches?.map(b => String(b.key))
        : null,
      guidInsurer: formInputs.insurer?.key
        ? String(formInputs.insurer.key)
        : null,
      guidVehicleManufacturer: formInputs.vehicleManufacturer
        ? String(formInputs.vehicleManufacturer)
        : null,
      guidVehicleModel: formInputs.vehicleModel
        ? String(formInputs.vehicleModel)
        : null,
      guidVehicleSubModel: formInputs.subModel
        ? String(formInputs.subModel)
        : null,
      idQuoteStatus: !!formInputs.idQuoteStatus
        ? formInputs.idQuoteStatus?.map(b => Number(b.key))
        : null,
      guidReason: formInputs.reason?.key ? String(formInputs.reason.key) : null,
      guidSupplier: formInputs.supplier?.key
        ? String(formInputs.supplier.key)
        : null,
      guidStates: !!formInputs.states
        ? formInputs.states?.map(b => String(b.key))
        : null,
      idPreQuoteType: formInputs.idPreQuoteType?.key
        ? Number(formInputs.idPreQuoteType.key)
        : null,
      havingItemCanceledBySupplier:
        formInputs.havingItemCanceledBySupplier || false,
      sortByOldest: formInputs.sortByOldest?.key
        ? Number(formInputs.sortByOldest.key)
        : undefined,
      pendingApproval: formInputs.pendingApproval || false,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    if (
      isUserInsurerLogged &&
      insurerReducer.data &&
      !insurerReducer.isLoading &&
      insurerReducer.data.length === 1
    ) {
      requestForm.guidInsurer = String(insurerReducer.data[0].key);
      setValue("insurer", insurerReducer.data[0])
    }

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);

    quoteApi.getSearchGrid(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results
      });
      onTotalNumberOfRecordsLoaded(response.data.totalNumberOfRecords);
      setHasShowMore(
        response.data.results.length < response.data.totalNumberOfRecords
      );
    });
    setShowFilters(false);
  }
  // This function is used to load the grid data when first loading the screen
  function loadInitialData(ignoreInitialLoadScreen: boolean = false) {
    if (
      (!isInitialLoadScreen || ignoreInitialLoadScreen) &&
      !isNullUndefined(parameterRangeDays) &&
      !reduxControls.isLoading &&
      reduxControls.data != null &&
      !preQuoteTypeReducer.isLoading &&
      preQuoteTypeReducer.data != null
    ) {
      const haveQueryParams = searchParams.toString() !== '';

      if (cardStatusQueryParams) {
        loadDataWithCardStatusParams();
      } // Load data with statusQueryParams
      else if (haveQueryParams) {
        handleUpdateQuoteCountStatus(
          queryParamsValues.startDate,
          queryParamsValues.finalDate
        );
      } // Load data with query params
      else {
        // Load data default
        const request = constructInitialRequest();
        const requestCount: QuoteCountStatusRequest = {
          startDate: initalDateForm(parameterRangeDays),
          finalDate: initalDateForm()
        };
        // Call grid by User logged
        if (
          isUserSupplierLogged &&
          supplierReducer.data &&
          !supplierReducer.isLoading &&
          supplierReducer.data.length === 1
        ) {
          request.supplier = supplierReducer.data[0];
        } else if (
          isUserInsurerLogged &&
          insurerReducer.data &&
          !insurerReducer.isLoading &&
          insurerReducer.data.length === 1
        ) {
          request.insurer = insurerReducer.data[0];
        } else if (
          isUserBodyShopLogged &&
          bodyshopReducer.data &&
          !bodyshopReducer.isLoading &&
          bodyshopReducer.data.length === 1
        ) {
          request.bodyshopBranches = bodyshopReducer.data;
        }
        dispatch(getQuoteCountStatus(requestCount));
        if (!preQuoteType || preQuoteTypeReducer.data.length === 1) {
          onSubmit(request);
        }
      }

      setIsInitialLoadScreen(true);
    }
  }

  function callInsurerApiDomain(
    bodyshopSelected?: KeyValue | KeyValue[] | null
  ) {
    // Clean fields & reducers
    if (isNullUndefined(optionDefaultCountryDrop)) return;

    dispatch(resetReducerInsurers());
    const request: InsurerFilterRequest = {
      guidCountry: String(optionDefaultCountryDrop?.key),
      type: HeadQuarterBranchFilterEnum.All,
      idStatusRegister: StatusRegisterEnum.Active
    };

    if (bodyshopSelected) {
      const bodyshops = Array.isArray(bodyshopSelected)
        ? bodyshopSelected
        : [bodyshopSelected];

      request.guidsBodyshop = bodyshops.map(x => String(x.key));
    }

    dispatch(getInsurers(request));
  }

  function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
    // Clean fields & reducers
    dispatch(resetReducerBodyshops());

    if (optionDefaultCountryDrop !== null) {
      const request: BodyShopFilterRequest = {
        ...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getBodyshops(request));
    }
  }

  function callSupplierApiDomain() {
    // Clean fields & reducers
    dispatch(resetReducerSuppliers());

    if (optionDefaultCountryDrop !== null) {
      const request: SupplierFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      dispatch(getSuppliers(request));
    }
  }

  function hasInvalidSortByOldestStatus(
    sortByOldest: KeyValue | null | undefined,
    status: KeyValue[] | null | undefined
  ) {
    const hasSortByOldest =
      !!sortByOldest?.key ||
      sortByOldest?.key === SortByOldestEnum.fillinarange;

    const hasMultipleStatus = (status?.length ?? 0) > 1;

    return hasSortByOldest && hasMultipleStatus;
  }
  //#endregion

  //#region HandleFunctions
  function handleClear() {
    reset(INITIAL_FORM_STATE);
    const hasSupplierDefault = userIsSupplier();
    if (hasSupplierDefault) setValue('supplier', optionDefaultSupplierDrop);
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);
    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
    clearErrors();
    setHasClear(true);
    setIsIgnoreValidationsDate(false);

    if (optionDefaultBodyshopDrop !== null) {
      setValue('bodyshopBranches', [optionDefaultBodyshopDrop]);
    }

    setGridData(INITIAL_GRID_STATE_ORDER_SCREEN);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);

    if (optionDefaultCountryDrop !== null) {
      callBodyshopApiDomain();
      callInsurerApiDomain();
    } else {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
    }

    if (preQuoteType) {
      const preQuoteKeyValue = {
        key: ProcurementStatusEnum.PreQuote,
        value: getTranslateByKey('information.prequote')
      };
      setValue('idQuoteStatus', [preQuoteKeyValue]);

      if (preQuoteTypeReducer.data?.length === 1) {
        setValue('idPreQuoteType', optionDefaultPreQuoteType);
      }
    }
  }

  const handleClickCard = (data: KeyValue) => {
    setValue('idQuoteStatus', [data]);
    handleClear();
    let request: ISearchQuoteForm = getValues();
    request.idQuoteStatus = [data];
    setSearchParams(
      {
        idCardStatus: String(data.key)
      },
      { replace: true }
    );
    onSubmit(request);
  };
  function handleIgnoreValidationsDate(isQueryParams: boolean = false) {
    const valueStartDate =
      isQueryParams && !isNullUndefined(queryParamsValues.startDate)
        ? queryParamsValues.startDate
        : null;
    const valueFinalDate =
      isQueryParams && !isNullUndefined(queryParamsValues.finalDate)
        ? queryParamsValues.finalDate
        : null;

    setValue('startDate', valueStartDate);
    setValue('finalDate', valueFinalDate);
  }

  function handleAcceptDateValidations(isQueryParams: boolean = false) {
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);
    const valueStartDate =
      isQueryParams && !isNullUndefined(queryParamsValues.startDate)
        ? queryParamsValues.startDate
        : firstDateResult;
    const valueFinalDate =
      isQueryParams && !isNullUndefined(queryParamsValues.finalDate)
        ? queryParamsValues.finalDate
        : secondDateResult;

    setValue('startDate', valueStartDate);
    setValue('finalDate', valueFinalDate);
    clearErrors(['startDate', 'finalDate']);
  }

  function handleOpenModalSavePredefinedSearch() {
    setOpenModalConfirmationSavePredefinedSearch(true);
  }

  function handleOpenModalDeletePredefinedSearch(option: KeyValue) {
    setPredefinedSearchToDelete(option);
    setOpenModalConfirmationDeletePredefinedSearch(true);
  }

  async function handleSavePredefinedSearch(description: string) {
    setloadingPredefinedSearch(true);

    trigger()
      .then(isValid => {
        if (!isValid) {
          setloadingPredefinedSearch(false);
          const filterIncalidMsg = getTranslateByKey(
            'information.fieldInvalid',
            true
          ).replace('{0}', getTranslateByKey('information.filters'));
          toast.error(filterIncalidMsg);
          setOpenModalConfirmationSavePredefinedSearch(false);
          return;
        }
        const rawValues = getValues();

        const sortByOldestKey = rawValues.sortByOldest?.key;
        const isSortByOldest =
          !isNullUndefined(sortByOldestKey) &&
          sortByOldestKey !== SortByOldestEnum.fillinarange;
        if (!isSortByOldest) {
          const ignoreValidationsDateSubmit =
            rawValues.quoteFilterVehicle.length >=
            PARAMETER_LENTGH_FILTER_CLAIM;
          const keysIgnoreValidationsDateSubmit = getKeysIgnoreValidationsDate(
            ignoreValidationsDateSubmit,
            rawValues.startDate,
            rawValues.finalDate
          );

          const rangeParameter = singleStatusSelected
            ? singleStatusSearchOrderDaysInterval
            : parameterRangeDays;

          validateFormDate(
            rawValues.startDate,
            rawValues.finalDate,
            rangeParameter,
            keysIgnoreValidationsDateSubmit
          );
        }

        if (
          hasInvalidSortByOldestStatus(
            rawValues.sortByOldest,
            rawValues.idQuoteStatus
          )
        ) {
          toast.error(
            getTranslateByKey(
              'information.favoriteSearchMultipleStatusSortByOldest'
            )
          );
          setloadingPredefinedSearch(false);
          setOpenModalConfirmationSavePredefinedSearch(false);
          return;
        }

        const parsed = formSchema.safeParse(rawValues);

        if (!parsed.success) {
          setloadingPredefinedSearch(false);
          setOpenModalConfirmationSavePredefinedSearch(false);
          return;
        }

        const request = parsed.data;

        saveUserPredefinedSearch(request, description)
          .then(result => {
            if (result) {
              toast.success(
                getTranslateByKey('information.userPredefinedSearchSavedSucess')
              );
              dispatch(getPredefinedSearch(RoutesConstants.orderSearchOrder));
            }
          })
          .finally(() => {
            setOpenModalConfirmationSavePredefinedSearch(false);
            setloadingPredefinedSearch(false);
          });
      })
      .catch(() => {
        setloadingPredefinedSearch(false);
        setOpenModalConfirmationSavePredefinedSearch(false);
      });
  }

  async function handleDeletePredefinedSearch() {
    if (!predefinedSearchToDelete) return;
    setloadingPredefinedSearch(true);

    const result = await deleteUserPredefinedSearch(
      predefinedSearchToDelete.key.toString()
    );

    if (result) {
      toast.success(
        getTranslateByKey('information.userPredefinedSearchDeletedSucess')
      );
      dispatch(getPredefinedSearch(RoutesConstants.orderSearchOrder));
    } else {
      toast.error(
        getTranslateByKey('information.userPredefinedSearchDeletedError')
      );
    }

    setOpenModalConfirmationDeletePredefinedSearch(false);
    setloadingPredefinedSearch(false);
  }

  async function handleSelectedPredefinedSearch(
    optionSelected: KeyValue | null
  ) {
    if (!optionSelected) return;

    const userPredefinedSearch = await getDetailsUserPredefinedSearch(
      optionSelected.key.toString()
    );
    if (!userPredefinedSearch) return;

    const currentPredefinedValue = optionSelected;

    reset(
      {
        ...INITIAL_FORM_STATE,
        ...userPredefinedSearch,
        predefinedSearch: currentPredefinedValue
      },
      { keepDefaultValues: true }
    );
    if (
      !userPredefinedSearch.sortByOldest &&
      (!userPredefinedSearch.quoteFilterVehicle ||
        !checkValidationDates(userPredefinedSearch.quoteFilterVehicle))
    ) {
      applyDefaultDates();
    }
  }

  function applyDefaultDates() {
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);

    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
  }

  async function handleSelectedFavoriteSearch() {
    const vehicleFilterBeforeAwait = getValues('quoteFilterVehicle');
    const userAlreadyEnteredClaimNumber =
      !isNullUndefined(vehicleFilterBeforeAwait) &&
      vehicleFilterBeforeAwait.length >= PARAMETER_LENTGH_FILTER_CLAIM;

    const favorite = showFavouriteSearches ? await getFavoriteSearch() : null;
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);

    if (favorite) {
      reset(favorite, { keepDefaultValues: true });
      if (
        !userAlreadyEnteredClaimNumber &&
        (!favorite.quoteFilterVehicle ||
          !checkValidationDates(favorite.quoteFilterVehicle)) &&
        !favorite.sortByOldest
      ) {
        setValue('startDate', firstDateResult);
        setValue('finalDate', secondDateResult);
      }

      if (favorite.vehicle) {
        setDefaultValueVehicleFavorite(favorite.vehicle);
      }
    } else {
      if (!userAlreadyEnteredClaimNumber) {
        setValue('startDate', firstDateResult);
        setValue('finalDate', secondDateResult);
      }
    }

    if (userIsInsurer()) setValue('insurer', optionDefaultInsurerDrop);
    if (
      userIsBodyshop() &&
      !favorite?.bodyshopBranches &&
      optionDefaultBodyshopDrop
    ) {
      setValue('bodyshopBranches', [optionDefaultBodyshopDrop]);
    }

    isAutoSubmit.current = true;

    const data = getValues();
    if (
      favorite &&
      (!data.quoteFilterVehicle ||
        checkValidationDates(data.quoteFilterVehicle))
    ) {
      return onSubmit(data, true);
    }
  }
  //#endregion

  //#region UseEffect's
  useEffect(() => {
    dispatch(getPreQuoteType());
    dispatch(getPredefinedSearch(RoutesConstants.orderSearchOrder));
  }, []);

  useEffect(() => {
    callSupplierApiDomain();
    callInsurerApiDomain();
    callBodyshopApiDomain();
    if (optionDefaultCountryDrop !== null) {
      const requestReason: ReasonFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        idReasonType: ReasonTypeEnum.QuoteChangeStatus
      };
      dispatch(getReason(requestReason));
      const requestState: StateFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key)
      };
      dispatch(getStatesUser(requestState));
    }
  }, [optionDefaultCountryDrop?.key]);

  useEffect(() => {
    if (insurerReducer.data && !insurerReducer.isLoading) {
      const isTypeCompanyUserInsurer = userIsInsurer();
      existsInDrop(insurerReducer.data, 'insurer');
      if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop)
        callBodyshopApiDomain(optionDefaultInsurerDrop);
    }
  }, [insurerReducer.data, insurerReducer.isLoading]);

  useEffect(() => {
    if (bodyshopReducer.data && !bodyshopReducer.isLoading) {
      const isTypeCompanyUserBodyshop = userIsBodyshop();
      existsInDrop(bodyshopReducer.data, 'bodyshopBranch');
      if (isTypeCompanyUserBodyshop && optionDefaultBodyshopDrop)
        callInsurerApiDomain(optionDefaultBodyshopDrop);
    }
  }, [bodyshopReducer.data, bodyshopReducer.isLoading]);

  useEffect(() => {
    if (!isNullUndefined(queryParamsValues.quoteFilterVehicle)) {
      const isIgnoreValidationDate = checkValidationDates(
        queryParamsValues.quoteFilterVehicle
      );
      if (!isNullUndefined(isIgnoreValidationDate)) {
        if (isIgnoreValidationDate) {
          handleIgnoreValidationsDate(true);
        } else {
          handleAcceptDateValidations(true);
        }
      }
    }
  }, [JSON.stringify(queryParamsValues)]);

  useEffect(() => {
    if (isRefreshGrid) {
      loadInitialData(true);
      setIsRefreshGrid(false);
    }
  }, [isRefreshGrid]);

  useEffect(() => {
    if (watcherCanceledBySupplier) {
      setValue('idQuoteStatus', []);
      setValue('reason', null);
    }
  }, [watcherCanceledBySupplier]);

  useEffect(() => {
    const loaded =
      !insurerReducer.isLoading &&
      insurerReducer.data !== null &&
      !bodyshopReducer.isLoading &&
      bodyshopReducer.data !== null &&
      !quoteStatusReducer.isLoading &&
      quoteStatusReducer.data !== null &&
      !stateReducer.isLoading &&
      stateReducer.data !== null;

    if (loaded) setReducersLoaded(true);
  }, [
    insurerReducer.isLoading,
    bodyshopReducer.isLoading,
    quoteStatusReducer.isLoading,
    stateReducer.isLoading
  ]);

  useEffect(() => {
    if (!reducersLoaded) return;
    if (didInitSearch.current) return;

    didInitSearch.current = true;

    const hasQueryParams = Object.keys(queryParamsValues).length > 0;

    if (hasQueryParams) {
      const data = getValues();

      if (data.vehicle) {
        setDefaultValueVehicleFavorite(data.vehicle);
      }

      return;
    }

    handleSelectedFavoriteSearch();
  }, [reducersLoaded]);

  useEffect(() => {
    const { firstDateResult, secondDateResult } =
      onLoadDateRangePicker(parameterRangeDays);

    setValue('startDate', firstDateResult);
    setValue('finalDate', secondDateResult);
  }, []);

  //#endregion

  //#region Render constants
  const statusCards = getStatusFiltered();
  const labelQuoteFilterVehicle = '{0} / {1} / {2} / {3}'
    .replace('{0}', getTranslateByKey('information.claimNr'))
    .replace('{1}', getTranslateByKey('information.orderNr'))
    .replace('{2}', getTranslateByKey('information.licensePlate'))
    .replace('{3}', getTranslateByKey('information.quoteNr'));
  //#endregion

  const singleStatusSelected = (watcherStatus.length ?? 0) == 1;

  const isOnlyAwaitingReleaseStatusSelected =
    !!watcherStatus &&
    watcherStatus.some(s => s.key == OrderStatusEnum.WaitingRelease);
  const isPredefinedDays =
    watcherSortByOldest?.key !== undefined && !watcherCanceledBySupplier;
  const blockDataRange =
    !!watcherSortByOldest &&
    watcherSortByOldest.key !== SortByOldestEnum.fillinarange;
  const rangeParameter = singleStatusSelected
    ? singleStatusSearchOrderDaysInterval
    : parameterRangeDays;

  const showReasonInput = !preQuoteType && (userIsInsurer() || userIsAdmin());
  const isDisabledReasonInput =
    !isOnlyAwaitingReleaseStatusSelected || watcherCanceledBySupplier;
  return (
    <>
      {showCards && statusCards.length > 0 && (
        <S.CardArea>
          {statusCards.map(headerStatus => {
            if (
              headerStatus.key != OrderStatusEnum.CancelProcess &&
              headerStatus.key != OrderStatusEnum.ReturnProcess &&
              headerStatus.key != OrderStatusEnum.ReturnApprovalProcess
            ) {
              return (
                <KOGCardStatus
                  key={headerStatus.key}
                  value={headerStatus.value}
                  idStatus={Number(headerStatus.key)}
                  numberOfValues={headerStatus.count}
                  onClick={() => handleClickCard(headerStatus)}
                />
              );
            }
          })}
        </S.CardArea>
      )}

      <Box
        showBox={showFilters}
        style={{ paddingBottom: 0 }}
        title={getTranslateByKey('information.filters')}>
        <form onSubmit={customHandleSubmit} noValidate>
          <S.Row>
            <S.SmallerInputLabel>
              <KogInputTextControlledNew
                name="quoteFilterVehicle"
                label={labelQuoteFilterVehicle}
                control={control}
                id="id-searchOrder-txt-quoteFilterVehicle"
                autoComplete="cc-csc"
                onChangeCustom={value => {
                  const isIgnoreValidationDate = checkValidationDates(value);
                  if (!isNullUndefined(isIgnoreValidationDate)) {
                    if (isIgnoreValidationDate) {
                      handleIgnoreValidationsDate();
                    } else {
                      handleAcceptDateValidations();
                    }
                  }
                }}
              />
            </S.SmallerInputLabel>
            <KogKeyboardDatePickerContainer
              idScreen="id-searchOrder"
              nameStartDate="startDate"
              nameFinalDate="finalDate"
              parameterRangeDays={rangeParameter}
              control={control}
              disabledFields={blockDataRange ? ['startDate', 'finalDate'] : []}
              ignoreKeyValidations={getKeysIgnoreValidationsDate(
                isIgnoreValidationsDate || blockDataRange,
                watch('startDate'),
                watch('finalDate')
              )}
            />
            <VehicleAutoCompleteComponent
              hasClearValue={hasClear}
              setHasClearValue={setHasClear}
              handleChangeVehicle={vehicle => {
                setValue(
                  'vehicleManufacturer',
                  vehicle?.guidVehicleManufacturer || ''
                );
                setValue('vehicleModel', vehicle?.guidVehicleModel || '');
                setValue('subModel', vehicle?.guidSubModel || '');
                setValue('vehicle', vehicle?.value || '');
              }}
              vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
              vehicleValueProps={watch('vehicle')}
            />
          </S.Row>
          <S.Row>
            <KogInputAutoCompleteCheckControlled
              name="bodyshopBranches"
              control={control}
              label={getTranslateByKey('information.bodyshop')}
              options={bodyshopReducer.data || []}
              customInputProps={{
                required: false,
                id: generateIdScreen('select-bodyshopbranches')
              }}
              disableSelectAll={false}
              selectAllLabel={getTranslateByKey('information.selectAll')}
              isLoading={bodyshopReducer.isLoading}
              disabled={isDisabledBodyshopDrop()}
              isDefaultValueUnique={isDisabledBodyshopDrop()}
              onChangeCustom={(value, optionChanged) => {
                if (optionChanged?.key === 'select-all') {
                  return;
                }

                if (!userIsInsurer()) {
                  callInsurerApiDomain(value);
                }
              }}
            />
            <S.FieldWrapper>
              <KogAutoCompleteControlled
                nameCustom="insurer"
                id="id-searchOrder-select-insurer"
                isDefaultValueUnique={optionDefaultInsurerDrop !== null}
                setValueCustom={setValue}
                label={getTranslateByKey('information.insurer')}
                control={control}
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={insurerReducer.data}
                isLoading={insurerReducer.isLoading}
                disabled={isDisabledInsurersDrop()}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  if (optionSelected === null) {
                    const bodyshop = getValues().bodyshopBranches;
                    bodyshop?.length > 0
                      ? callInsurerApiDomain(bodyshop)
                      : callInsurerApiDomain();
                  }
                  callBodyshopApiDomain(optionSelected);
                }}
              />
            </S.FieldWrapper>
            <KogAutoCompleteControlled
              nameCustom="supplier"
              control={control}
              isDefaultValueUnique
              setValueCustom={setValue}
              id="id-searchOrder-select-supplier"
              label={getTranslateByKey('information.supplier')}
              rulesCustom={{ required: false }}
              options={supplierReducer.data}
              isLoading={supplierReducer.isLoading}
              disabled={isDisabledSupplierDrop()}
              onChangeCustom={() => {}}
            />
            {!userIsBodyshop() && (
              <KogInputAutoCompleteCheckControlled
                name="states"
                control={control}
                label={getTranslateByKey('information.deliveryAddressState')}
                options={stateReducer.data || []}
                customInputProps={{
                  required: false,
                  id: generateIdScreen('select-state')
                }}
                disableSelectAll={false}
                selectAllLabel={getTranslateByKey('information.selectAll')}
                isLoading={stateReducer.isLoading}
              />
            )}
            <S.FieldWrapper>
              <KogInputAutoCompleteCheckControlled
                name="idQuoteStatus"
                control={control}
                label={getTranslateByKey('information.status')}
                options={getStatusDropFiltered() || []}
                customInputProps={{
                  required: isPredefinedDays,
                  id: generateIdScreen('select-idQuoteStatus')
                }}
                controllerProps={{ rules: { required: isPredefinedDays } }}
                disableSelectAll={false}
                required={isPredefinedDays}
                selectAllLabel={getTranslateByKey('information.selectAll')}
                isLoading={quoteStatusReducer.isLoading}
                disabled={isDisabledStatusDrop()}
                onChangeCustom={value => {
                  setValue('reason', null);
                }}
              />
            </S.FieldWrapper>
            {showReasonInput && (
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.reason')}
                control={control}
                nameCustom="reason"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={reasonReducer.data}
                isLoading={reasonReducer.isLoading}
                onChangeCustom={() => {}}
                disabled={isDisabledReasonInput}
                id="id-searchOrder-select-reason"
              />
            )}
            {preQuoteType && (
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.type')}
                control={control}
                nameCustom="idPreQuoteType"
                rulesCustom={{ required: true }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={preQuoteTypeReducer.data}
                isLoading={preQuoteTypeReducer.isLoading}
                disabled={isDisabledPreQuoteTypeDrop()}
                onChangeCustom={() => {}}
                isDefaultValueUnique
                setValueCustom={setValue}
                id="id-searchOrder-prequote-type"
              />
            )}
            {showSortByOldest && (
              <KogAutoCompleteControlled
                id={generateIdScreen('select-sortByOldest')}
                nameCustom="sortByOldest"
                label={getTranslateByKey('information.sortByOldest')}
                control={control}
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={sortByOldestOptions}
                isLoading={false}
                disabled={false}
                onChangeCustom={(valueSelected: KeyValue | null) => {
                  setValue('sortByOldest', valueSelected);

                  if (valueSelected) {
                    setValue('startDate', null);
                    setValue('finalDate', null);
                    setIsIgnoreValidationsDate(true);
                  } else {
                    const { firstDateResult, secondDateResult } =
                      onLoadDateRangePicker(parameterRangeDays);

                    setValue('startDate', firstDateResult);
                    setValue('finalDate', secondDateResult);
                    setIsIgnoreValidationsDate(false);
                  }
                }}
              />
            )}
            {showPredefinedSearches && (
              <KogAutoCompleteControlledWithDelete
                label={getTranslateByKey('information.predefinedSearches')}
                control={control}
                nameCustom="predefinedSearch"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={domainPredefinedSearch.data}
                isLoading={domainPredefinedSearch.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  handleSelectedPredefinedSearch(optionSelected);
                }}
                id="id-searchCartsPendingToAnswer-predefinedSearches"
                onDeleteCustom={(option: KeyValue) => {
                  handleOpenModalDeletePredefinedSearch(option);
                }}
              />
            )}
          </S.Row>
          <S.RowBottom>
            {(showFavouriteSearches || showPredefinedSearches) && (
              <S.IconArea>
                <S.FavPredefinedGroup $stacked={isWrapped}>
                  {showFavouriteSearches && (
                    <RenderTooltipMessage
                      tooltipMessage={getTranslateByKey(
                        'information.favoriteSearchTooltipMessage'
                      )}>
                      <S.FavoriteSearchLabel
                        id={generateIdScreen('favorite-searchClaim')}
                        onClick={handleOpenModalFavoriteSearch}
                        control={
                          <FavoriteBorderIcon
                            fontSize="medium"
                            color="primary"
                            style={{ marginRight: 8, marginLeft: 10 }}
                          />
                        }
                        label={getTranslateByKey(
                          'information.favoriteSearchClaim'
                        )}
                      />
                    </RenderTooltipMessage>
                  )}
                  {showPredefinedSearches && (
                    <RenderTooltipMessage
                      tooltipMessage={getTranslateByKey(
                        'information.predefinedSearchTooltipMessage'
                      )}>
                      <S.PredefinedSearchLabel
                        onClick={handleOpenModalSavePredefinedSearch}
                        control={
                          <SearchIcon
                            fontSize="medium"
                            color="primary"
                            style={{ marginRight: 6, marginLeft: 12 }}
                          />
                        }
                        label={getTranslateByKey(
                          'information.predefinedSearch'
                        )}
                        id="id-searchCartsPendingToAnswer-predefinedSearch"
                      />
                    </RenderTooltipMessage>
                  )}
                </S.FavPredefinedGroup>
              </S.IconArea>
            )}
            <S.IconArea ref={checkboxAreaRef} style={{ gridColumn: 2 }}>
              {isShowControlByElementId(
                Controls.IdFilterCanceledBySupplier
              ) && (
                <S.GridCheckbox>
                  <KogCheckBoxControlled
                    tooltipMessage={getTranslateByKey(
                      'information.filterOrdersWithCancelledParts'
                    )}
                    nameCustom="havingItemCanceledBySupplier"
                    name="havingItemCanceledBySupplier"
                    control={control}
                    label={getTranslateByKey(
                      'information.filterCanceledBySupplier'
                    )}
                    id="id-filter-checkBox-canceledBySupplier"
                  />
                </S.GridCheckbox>
              )}

              {showPendingApproval && (
                <S.GridCheckbox>
                  <KogCheckBoxControlled
                    tooltipMessage={getTranslateByKey(
                      'information.filterQuotesPendingApprovalForLoggedUser'
                    )}
                    nameCustom="pendingApproval"
                    onChangeCustom={(value: boolean) => {
                      if (value) {
                        const awaitingApprovalKV = {
                          key: ProcurementStatusEnum.AwaitingApproval,
                          value: getTranslateByKey(
                            'procurementstatus.awaitingApproval'
                          )
                        };
                        setValue('idQuoteStatus', [awaitingApprovalKV]);
                      } else {
                        setValue('idQuoteStatus', []);
                      }
                    }}
                    name="pendingApproval"
                    control={control}
                    label={getTranslateByKey('information.pendingApproval')}
                    id="id-filter-checkBox-pendingApproval"
                  />
                </S.GridCheckbox>
              )}
            </S.IconArea>
            <S.ButtonsWrapper>
              <SecondaryButton type="submit" id="id-searchOrder-btn-submit">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchOrder-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </S.ButtonsWrapper>
          </S.RowBottom>
        </form>
        <BasicModal
          open={openModalConfirmationFavoriteSearch}
          title={getTranslateByKey(
            'information.userPreferenceFavoriteSearchTitle'
          )}
          subtitle={getTranslateByKey(
            'information.userPreferenceFavoriteSearchModalConfirmationMessage'
          )}
          handleClose={() => setOpenModalConfirmationFavoriteSearch(false)}>
          <ButtonsContainer>
            <SecondaryButton
              id={generateIdScreen('btn-cancelSaveFavoriteSearch')}
              variant="outlined"
              onClick={() => setOpenModalConfirmationFavoriteSearch(false)}
              disabled={loadingFavoriteSearch}>
              {getTranslateByKey('information.no')}
            </SecondaryButton>

            <Button
              id={generateIdScreen('btn-saveFavoriteSearch')}
              variant="contained"
              onClick={handleConfirmModalFavoriteSearch}
              isLoading={loadingFavoriteSearch}>
              {getTranslateByKey('information.yes')}
            </Button>
          </ButtonsContainer>
        </BasicModal>
      </Box>
      <ModalSavePredefinedSearch
        open={openModalConfirmationSavePredefinedSearch}
        titleModal={getTranslateByKey('information.savePredefinedSearchTitle')}
        handleModalCancel={() =>
          setOpenModalConfirmationSavePredefinedSearch(false)
        }
        handleModalConfirm={description =>
          handleSavePredefinedSearch(description)
        }
        isLoading={loadingPredefinedSearch}
      />
      <ModalDeletePredefinedSearch
        open={openModalConfirmationDeletePredefinedSearch}
        predefinedSearchName={predefinedSearchToDelete?.value ?? ''}
        handleModalCancel={() =>
          setOpenModalConfirmationDeletePredefinedSearch(false)
        }
        handleModalConfirm={handleDeletePredefinedSearch}
        isLoading={loadingPredefinedSearch}
      />
    </>
  );
}
function FiltersOrderScreenWrapper(
  props: Omit<
    IFiltersOrderScreenProps,
    'showFilters' | 'setShowFilters' | 'isRefreshGrid' | 'setIsRefreshGrid'
  >
) {
  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [isOpenModalImportQuote, setIsOpenModalImportQuote] = useState(false);
  const [isRefreshGrid, setIsRefreshGrid] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const importModalQueryParams = searchParams.get('importModal');
  const showImportQuote = useKogControlElementId(Controls.IdImportQuote);
  //#endregion

  useEffect(() => {
    if (importModalQueryParams == 'true') {
      setIsOpenModalImportQuote(true);
    }
  }, []);

  //#region HandleFunctions
  function handleModalImportQuote() {
    setIsOpenModalImportQuote(prev => !prev);
  }
  function handleOpenModalImportQuote() {
    if (isOpenModalImportQuote) return;
    setIsOpenModalImportQuote(true);
  }
  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  //#endregion

  const mergedProps: IFiltersOrderScreenProps = {
    ...props,
    isRefreshGrid,
    showFilters,
    setShowFilters,
    setIsRefreshGrid
  };
  return (
    <>
      <S.CustomAreaButton>
        {showImportQuote && (
          <Button
            onClick={handleModalImportQuote}
            id="id-searchOrder-btn-importQuote">
            {getTranslateByKey('information.importQuote')}
          </Button>
        )}

        <Button onClick={handleFilters} id="id-searchOrder-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
      </S.CustomAreaButton>
      <FiltersOrderScreen {...mergedProps} />

      <KogModalSideComponent
        titleModalReducer="modalImportQuote"
        open={isOpenModalImportQuote}
        closeModal={() => setIsOpenModalImportQuote(false)}>
        <ModalImportQuote
          isOpenModal={isOpenModalImportQuote}
          handleOpenModalImportQuote={handleOpenModalImportQuote}
          handleModalImportQuote={handleModalImportQuote}
          loadInitialData={() => setIsRefreshGrid(true)}
        />
      </KogModalSideComponent>
    </>
  );
}
export default FiltersOrderScreenWrapper;
